import { useCallback, useRef, useEffect } from 'react';
import { toast } from '@/hooks/useToast';

interface UseAutoSaveOptions<T> {
  debounceTime?: number;
  showToast?: boolean;
  saveFunction: (data: T) => Promise<{ success: boolean; error?: string }>;
  onSuccess?: (data: T) => void;
  onError?: (error: string) => void;
}

export const useAutoSave = <T>(options: UseAutoSaveOptions<T>) => {
  const { 
    debounceTime = 400, 
    showToast = false, 
    saveFunction, 
    onSuccess, 
    onError 
  } = options;
  
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastSavedDataRef = useRef<string>('');

  const saveWithDebounce = useCallback(async (data: T) => {
    // Skip if data is empty or null
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create a hash of current data
    const currentDataHash = JSON.stringify(data);
    
    // Skip if data hasn't actually changed
    if (currentDataHash === lastSavedDataRef.current) return;

    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(async () => {
      // Double-check that data has changed
      const finalDataHash = JSON.stringify(data);
      if (finalDataHash === lastSavedDataRef.current) return;
      
      try {
        const result = await saveFunction(data);
        
        if (result.success) {
          lastSavedDataRef.current = finalDataHash;
          
          // Call success callback
          if (onSuccess) {
            onSuccess(data);
          }
          
          if (showToast) {
            toast({
              title: "Salvato",
              description: "Modifiche salvate automaticamente",
            });
          }
        } else {
          // Call error callback
          if (onError) {
            onError(result.error || "Errore sconosciuto");
          }
          
          if (showToast) {
            toast({
              title: "Errore",
              description: result.error || "Impossibile salvare le modifiche",
              variant: "destructive",
            });
          }
        }
      } catch (error) {
        console.error('Auto-save error:', error);
        
        const errorMessage = error instanceof Error ? error.message : "Errore durante il salvataggio automatico";
        
        // Call error callback
        if (onError) {
          onError(errorMessage);
        }
        
        if (showToast) {
          toast({
            title: "Errore",
            description: errorMessage,
            variant: "destructive",
          });
        }
      }
    }, debounceTime);
  }, [saveFunction, debounceTime, showToast, onSuccess, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { saveWithDebounce };
};
